import type {
  Point,
  TransformState,
} from "../../../../../../types/stage-types.js";

/**
 * Grid configuration options
 */
export interface GridConfig {
  // Dot grid settings
  readonly dotSpacing?: number;
  readonly dotSize?: number;
  readonly dotOpacity?: number;
  readonly majorDotOpacity?: number;

  // Linear grid settings
  readonly lineSpacing?: number;
  readonly lineOpacity?: number;

  // Major grid settings
  readonly majorGridInterval?: number;

  // Drawing area settings
  readonly widthOffset?: number;

  // Visual settings
  readonly dotColor?: string;
  readonly lineColor?: string;
}

/**
 * Grid state interface
 */
interface GridState {
  readonly dotGridVisible: boolean;
  readonly linearGridVisible: boolean;
  readonly currentTransform: TransformState;
}

/**
 * Drawing bounds for grid rendering
 */
interface DrawingBounds {
  readonly left: number;
  readonly right: number;
  readonly width: number;
}

/**
 * GridManager API for controlling grid display and behavior
 */
export interface GridManagerAPI {
  readonly setDotGridVisible: (visible: boolean) => void;
  readonly setLinearGridVisible: (visible: boolean) => void;
  readonly setGridSpacing: (spacing: number) => void;
  readonly updateTransform: (transform: TransformState) => void;
  readonly render: () => void;
  readonly adjustConfig: (updates: Partial<GridConfig>) => void;
  readonly getConfig: () => Readonly<GridConfig>;
  readonly resetConfig: () => void;
  readonly getState: () => Readonly<GridState>;
  readonly destroy: () => void;
}

/**
 * Creates a GridManager for handling dot and linear grid display
 * @param container - Container element for grid canvas
 * @param config - Grid configuration options
 * @returns GridManager API
 */
export const createGridManager = (
  container: HTMLElement,
  config: GridConfig = {}
): GridManagerAPI => {
  console.log("📊 Creating GridManager");

  let isDestroyed = false;

  // Default grid configuration
  const defaultConfig: Required<GridConfig> = {
    // Dot grid settings
    dotSpacing: config.dotSpacing || 18,
    dotSize: config.dotSize || 2,
    dotOpacity: config.dotOpacity || 0.4,
    majorDotOpacity: config.majorDotOpacity || 0.6,

    // Linear grid settings
    lineSpacing: config.lineSpacing || 100,
    lineOpacity: config.lineOpacity || 0.3,

    // Major grid settings
    majorGridInterval: config.majorGridInterval || 100,

    // Drawing area settings
    widthOffset: config.widthOffset || 25,

    // Visual settings
    dotColor: config.dotColor || "183, 183, 183",
    lineColor: config.lineColor || "100, 100, 100",
  };

  // Current grid configuration (mutable for adjustments)
  let gridConfig = { ...defaultConfig };

  // Grid state
  let state: GridState = {
    dotGridVisible: false,
    linearGridVisible: false,
    currentTransform: {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
    },
  };

  // Canvas for grid rendering
  let gridCanvas: HTMLCanvasElement | null = null;
  let gridCtx: CanvasRenderingContext2D | null = null;

  /**
   * Create and setup the grid canvas
   */
  const createGridCanvas = (): void => {
    if (gridCanvas) return; // Already created

    gridCanvas = document.createElement("canvas");
    gridCtx = gridCanvas.getContext("2d");

    if (!gridCtx) {
      console.error("Failed to get 2D context for grid canvas");
      return;
    }

    gridCanvas.className = "grid-manager__canvas";
    gridCanvas.style.position = "absolute";
    gridCanvas.style.top = "0";
    gridCanvas.style.left = "0";
    gridCanvas.style.pointerEvents = "none";

    container.appendChild(gridCanvas);
  };

  /**
   * Setup canvas with retina support for crisp rendering
   */
  const setupRetinaCanvas = (width: number, height: number): void => {
    if (!gridCanvas || !gridCtx) return;

    const devicePixelRatio = window.devicePixelRatio || 1;

    // Set actual canvas size (retina resolution)
    gridCanvas.width = width * devicePixelRatio;
    gridCanvas.height = height * devicePixelRatio;

    // Scale canvas back down using CSS
    gridCanvas.style.width = width + "px";
    gridCanvas.style.height = height + "px";

    // Scale the drawing context
    gridCtx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    gridCtx.imageSmoothingEnabled = false;
  };

  /**
   * Calculate the coordinate origin based on product container position
   */
  const calculateCoordinateOrigin = (): Point => {
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;

    if (!productContainer) {
      return {
        x: state.currentTransform.translateX,
        y: state.currentTransform.translateY,
      };
    }

    const containerRect = productContainer.getBoundingClientRect();
    const stageRect = container.getBoundingClientRect();

    const containerLeft = containerRect.left - stageRect.left;
    const containerTop = containerRect.top - stageRect.top;

    return {
      x: containerLeft / state.currentTransform.scale,
      y: containerTop / state.currentTransform.scale,
    };
  };

  /**
   * Calculate drawing bounds based on product area + configurable offset
   */
  const calculateDrawingBounds = (): DrawingBounds => {
    const origin = calculateCoordinateOrigin();
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;

    if (!productContainer || !gridCanvas) {
      const canvasWidth = gridCanvas?.width
        ? gridCanvas.width / (window.devicePixelRatio || 1)
        : 1000;
      return {
        left: 0,
        right: canvasWidth,
        width: canvasWidth,
      };
    }

    const productRect = productContainer.getBoundingClientRect();
    const productWidth = productRect.width;
    const buffer = productWidth * (gridConfig.widthOffset / 100);
    const originX = origin.x * state.currentTransform.scale;

    return {
      left: originX - buffer,
      right: originX + productWidth + buffer,
      width: productWidth * (1 + gridConfig.widthOffset / 50),
    };
  };

  /**
   * Render dot grid
   */
  const renderDotGrid = (): void => {
    if (isDestroyed || !gridCanvas || !gridCtx || !state.dotGridVisible) return;

    const canvasWidth = gridCanvas.width / (window.devicePixelRatio || 1);
    const canvasHeight = gridCanvas.height / (window.devicePixelRatio || 1);

    const origin = calculateCoordinateOrigin();
    const originX = origin.x * state.currentTransform.scale;
    const originY = origin.y * state.currentTransform.scale;
    const dotSpacing = gridConfig.dotSpacing * state.currentTransform.scale;
    const majorGridSpacing =
      gridConfig.majorGridInterval * state.currentTransform.scale;

    const bounds = calculateDrawingBounds();

    // Calculate grid bounds
    const dotsLeft = Math.ceil((originX - bounds.left) / dotSpacing);
    const dotsRight = Math.ceil((bounds.right - originX) / dotSpacing);
    const dotsUp = Math.ceil(originY / dotSpacing);
    const dotsDown = Math.ceil((canvasHeight - originY) / dotSpacing);

    // Draw grid of dots
    for (let i = -dotsLeft; i <= dotsRight; i++) {
      for (let j = -dotsUp; j <= dotsDown; j++) {
        const x = originX + i * dotSpacing;
        const y = originY + j * dotSpacing;

        // Check bounds
        if (
          x >= bounds.left &&
          x <= bounds.right &&
          y >= 0 &&
          y <= canvasHeight &&
          x >= 0 &&
          x <= canvasWidth
        ) {
          // Check if major grid point
          const xDistanceFromOrigin = Math.abs(x - originX);
          const yDistanceFromOrigin = Math.abs(y - originY);
          const isXMajor = Math.abs(xDistanceFromOrigin % majorGridSpacing) < 1;
          const isYMajor = Math.abs(yDistanceFromOrigin % majorGridSpacing) < 1;
          const isMajorGridPoint = isXMajor || isYMajor;

          // Set appropriate opacity
          if (isMajorGridPoint) {
            gridCtx.fillStyle = `rgba(${gridConfig.dotColor}, ${gridConfig.majorDotOpacity})`;
          } else {
            gridCtx.fillStyle = `rgba(${gridConfig.dotColor}, ${gridConfig.dotOpacity})`;
          }

          gridCtx.beginPath();
          gridCtx.arc(x, y, gridConfig.dotSize / 2, 0, 2 * Math.PI);
          gridCtx.fill();
        }
      }
    }
  };

  /**
   * Render linear grid
   */
  const renderLinearGrid = (): void => {
    if (isDestroyed || !gridCanvas || !gridCtx || !state.linearGridVisible)
      return;

    const origin = calculateCoordinateOrigin();
    const canvasHeight = gridCanvas.height / (window.devicePixelRatio || 1);
    const canvasWidth = gridCanvas.width / (window.devicePixelRatio || 1);

    const lineSpacing = gridConfig.lineSpacing * state.currentTransform.scale;
    const originX = origin.x * state.currentTransform.scale;
    const originY = origin.y * state.currentTransform.scale;

    const bounds = calculateDrawingBounds();

    const originalLineWidth = gridCtx.lineWidth;
    const originalStrokeStyle = gridCtx.strokeStyle;

    // Set line properties
    gridCtx.lineWidth = 1;
    gridCtx.strokeStyle = `rgba(${gridConfig.lineColor}, ${gridConfig.lineOpacity})`;
    gridCtx.setLineDash([]);

    // Draw vertical lines from origin
    for (
      let x = originX;
      x <= bounds.right && x <= canvasWidth;
      x += lineSpacing
    ) {
      if (x >= bounds.left) {
        gridCtx.beginPath();
        gridCtx.moveTo(x, 0);
        gridCtx.lineTo(x, canvasHeight);
        gridCtx.stroke();
      }
    }

    // Draw vertical lines to the left of origin
    for (
      let x = originX - lineSpacing;
      x >= bounds.left && x >= 0;
      x -= lineSpacing
    ) {
      gridCtx.beginPath();
      gridCtx.moveTo(x, 0);
      gridCtx.lineTo(x, canvasHeight);
      gridCtx.stroke();
    }

    // Draw horizontal lines below origin
    for (let y = originY; y <= canvasHeight; y += lineSpacing) {
      gridCtx.beginPath();
      gridCtx.moveTo(Math.max(bounds.left, 0), y);
      gridCtx.lineTo(Math.min(bounds.right, canvasWidth), y);
      gridCtx.stroke();
    }

    // Draw horizontal lines above origin
    for (let y = originY - lineSpacing; y >= 0; y -= lineSpacing) {
      gridCtx.beginPath();
      gridCtx.moveTo(Math.max(bounds.left, 0), y);
      gridCtx.lineTo(Math.min(bounds.right, canvasWidth), y);
      gridCtx.stroke();
    }

    // Restore original properties
    gridCtx.lineWidth = originalLineWidth;
    gridCtx.strokeStyle = originalStrokeStyle;
  };

  /**
   * Main render function
   */
  const render = (): void => {
    if (isDestroyed) return;

    // Only render if grids are visible
    if (!state.dotGridVisible && !state.linearGridVisible) {
      if (gridCanvas && gridCtx) {
        gridCtx.clearRect(0, 0, gridCanvas.width, gridCanvas.height);
      }
      return;
    }

    // Ensure canvas exists
    if (!gridCanvas) {
      createGridCanvas();
    }

    if (!gridCanvas || !gridCtx) return;

    // Get container dimensions
    const rect = container.getBoundingClientRect();
    const width = rect.width || 1000;
    const height = rect.height || 1000;

    // Setup canvas with retina support
    setupRetinaCanvas(width, height);

    // Clear canvas
    gridCtx.clearRect(0, 0, width, height);

    // Render linear grid first (behind dots)
    if (state.linearGridVisible) {
      renderLinearGrid();
    }

    // Render dot grid
    if (state.dotGridVisible) {
      renderDotGrid();
    }
  };

  /**
   * Set dot grid visibility
   */
  const setDotGridVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, dotGridVisible: visible };
    render();
  };

  /**
   * Set linear grid visibility
   */
  const setLinearGridVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, linearGridVisible: visible };
    render();
  };

  /**
   * Set grid spacing
   */
  const setGridSpacing = (spacing: number): void => {
    if (isDestroyed) return;

    gridConfig.dotSpacing = Math.max(5, spacing);
    render();
  };

  /**
   * Update transform state
   */
  const updateTransform = (transform: TransformState): void => {
    if (isDestroyed) return;

    state = { ...state, currentTransform: { ...transform } };
    render();
  };

  /**
   * Adjust grid configuration
   */
  const adjustConfig = (updates: Partial<GridConfig>): void => {
    if (isDestroyed) return;

    Object.assign(gridConfig, updates);
    console.log("Grid config updated:", gridConfig);
    render();
  };

  /**
   * Get current grid configuration
   */
  const getConfig = (): Readonly<GridConfig> => ({ ...gridConfig });

  /**
   * Reset grid configuration to defaults
   */
  const resetConfig = (): void => {
    if (isDestroyed) return;

    gridConfig = { ...defaultConfig };
    console.log("Grid config reset to defaults");
    render();
  };

  /**
   * Get current state
   */
  const getState = (): Readonly<GridState> => ({ ...state });

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    if (gridCanvas && gridCanvas.parentNode) {
      gridCanvas.parentNode.removeChild(gridCanvas);
    }

    gridCanvas = null;
    gridCtx = null;
  };

  // Initialize - create canvas will be called on first render
  console.log("GridManager initialized");

  return {
    setDotGridVisible,
    setLinearGridVisible,
    setGridSpacing,
    updateTransform,
    render,
    adjustConfig,
    getConfig,
    resetConfig,
    getState,
    destroy,
  };
};
